import { ref } from 'vue';
import { useStore } from './store.service';
const store = useStore();

type NotificationType = 'info' | 'success' | 'warning' | 'error';

type Notification = {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
  persistent?: boolean;
};

// Global notifications state
const notifications = ref<Notification[]>([]);

// Generate unique ID for notifications
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const useNotifications = () => {
  const NOTIFICATION_DURATION_S = Number(store.readConfig<number>('NOTIFICATION_DURATION_S')) || 8;

  const addNotification = (
    message: string,
    type: NotificationType = 'info',
    duration: number = NOTIFICATION_DURATION_S,
    persistent = false
  ): string => {
    const id = generateId();
    
    const notification: Notification = {
      id,
      message,
      type,
      duration,
      persistent,
    };

    notifications.value.push(notification);

    // Auto-remove notification after duration (unless persistent)
    if (!persistent && duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration * 1000);
    }

    return id;
  };

  const removeNotification = (id: string): void => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearAllNotifications = (): void => {
    notifications.value = [];
  };

  // Convenience methods for different notification types
  const showSuccess = (
    message: string, 
    duration: number = NOTIFICATION_DURATION_S
  ): string => {
    return addNotification(message, 'success', duration);
  };

  const showError = (
    message: string, 
    duration: number = NOTIFICATION_DURATION_S
  ): string => {
    return addNotification(message, 'error', duration);
  };

  const showInfo = (
    message: string, 
    duration: number = NOTIFICATION_DURATION_S
  ): string => {
    return addNotification(message, 'info', duration);
  };

  const showWarning = (
    message: string, 
    duration: number = NOTIFICATION_DURATION_S
  ): string => {
    return addNotification(message, 'warning', duration);
  };

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };
};
